import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getURL } from '../../utils/api';

const inputBase =
    'w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:ring-2';
const inputClass = (error) =>
    `${inputBase} ${error ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-100' : 'border-slate-300 focus:border-violet-600 focus:ring-violet-100'}`;

// One question inside a card: label, control, hint / error.
function Field({ label, required, hint, error, wide = false, children }) {
    return (
        <div className={wide ? 'sm:col-span-2' : ''} data-invalid={error ? 'true' : undefined}>
            <label className="block text-sm font-medium text-slate-800">
                {label}
                {required ? <span className="ml-0.5 text-rose-600">*</span> : null}
            </label>
            <div className="mt-1.5">{children}</div>
            {error ? (
                <p className="mt-1 text-xs text-rose-600">{error}</p>
            ) : hint ? (
                <p className="mt-1 text-xs text-slate-500">{hint}</p>
            ) : null}
        </div>
    );
}

// A group of related questions: numbered header + body.
function Card({ number, title, description, children }) {
    return (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <header className="flex items-center gap-3 border-b border-slate-100 bg-violet-50/60 px-5 py-3.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-600 text-sm font-semibold text-white">
                    {number}
                </span>
                <div className="min-w-0">
                    <h2 className="text-base font-semibold text-slate-900">{title}</h2>
                    <p className="text-xs text-slate-600">{description}</p>
                </div>
            </header>
            <div className="grid grid-cols-1 gap-x-5 gap-y-4 p-5 sm:grid-cols-2">{children}</div>
        </section>
    );
}

// Radio options shown as selectable chips.
function Choices({ name, options, value, onChange }) {
    return (
        <div className="flex flex-wrap gap-2">
            {options.map((option) => {
                const selected = value === option;
                return (
                    <label
                        key={option}
                        className={`cursor-pointer rounded-full border px-4 py-1.5 text-sm transition ${
                            selected
                                ? 'border-violet-600 bg-violet-600 font-medium text-white'
                                : 'border-slate-300 bg-white text-slate-700 hover:border-violet-400'
                        }`}
                    >
                        <input
                            type="radio"
                            name={name}
                            value={option}
                            checked={selected}
                            onChange={onChange}
                            className="sr-only"
                        />
                        {option}
                    </label>
                );
            })}
        </div>
    );
}

function Shell({ children }) {
    return (
        <div className="min-h-screen bg-violet-50 px-3 py-6 sm:py-10">
            <div className="mx-auto w-full max-w-2xl space-y-4">{children}</div>
        </div>
    );
}

function Header({ locked = false }) {
    return (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="h-2.5 bg-violet-600" />
            <div className="p-5 sm:p-6">
                <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Course Inquiry Form</h1>
                <p className="mt-2 text-sm text-slate-600">
                    {locked ? 'Leave your details for the course below. Our counsellor will contact you.' : 'Choose the course you are interested in and leave your details. Our counsellor will contact you.'}
                </p>
                <p className="mt-3 text-xs text-rose-600">* Required</p>
            </div>
        </div>
    );
}

const EMPTY = {
    studentName: '',
    mobile: '',
    whatsapp: '',
    nic: '',
    email: '',
    city: '',
    educationLevel: '',
    gender: '',
    dateOfBirth: '',
    occupation: '',
    message: '',
    website: '',
};

const EDUCATION_LEVELS = ['O/L', 'A/L', 'Diploma', 'Degree', 'Postgraduate', 'Other'];
const GENDERS = ['Male', 'Female', 'Other'];

const isValidPhone = (value) => /^(0\d{9}|94\d{9})$/.test(String(value).replace(/\D+/g, ''));
const isValidNic = (value) => /^(\d{9}[VX]|\d{12})$/.test(String(value).trim().toUpperCase());
const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value).trim());
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function PublicInquiryForm() {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const apiBase = getURL();
    const presetToken = searchParams.get('c');

    const [state, setState] = useState('loading'); // loading | ready | invalid | error
    const [courses, setCourses] = useState([]);
    // Encrypted course token -- the real course id never appears in the URL or page.
    const [courseToken, setCourseToken] = useState(null);
    // Set when the counsellor shared a course-specific link (?c=TOKEN): the course is fixed.
    const [lockedCourse, setLockedCourse] = useState(false);
    const [courseSearch, setCourseSearch] = useState('');
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState('');
    const [doneMessage, setDoneMessage] = useState('');

    useEffect(() => {
        let cancelled = false;
        axios
            .get(`${apiBase}/sales-inquiry-form/public/${encodeURIComponent(slug)}`, {
                params: presetToken ? { c: presetToken } : undefined,
            })
            .then((res) => {
                if (cancelled) return;
                const list = res.data?.courses ?? [];
                setCourses(list);
                if (res.data?.locked && list[0]) {
                    setCourseToken(list[0].token);
                    setLockedCourse(true);
                }
                setState('ready');
            })
            .catch((err) => {
                if (cancelled) return;
                setState(err?.response?.status === 404 ? 'invalid' : 'error');
            });
        return () => {
            cancelled = true;
        };
    }, [apiBase, slug]); // eslint-disable-line react-hooks/exhaustive-deps

    const filteredCourses = useMemo(() => {
        const q = courseSearch.trim().toLowerCase();
        if (!q) return courses;
        return courses.filter((c) =>
            [c.shortName, c.fullName].filter(Boolean).some((v) => v.toLowerCase().includes(q)),
        );
    }, [courses, courseSearch]);

    const selectedCourse = courses.find((c) => c.token === courseToken) ?? null;

    const setField = (field) => (event) => {
        const value = event.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const findErrors = () => {
        const next = {};
        const name = form.studentName.trim();
        if (!courseToken) next.courseToken = 'Please select a course.';
        if (name.length < 3 || !/^[\p{L}][\p{L} .'-]*$/u.test(name)) next.studentName = 'Please enter your full name (letters only).';
        if (!isValidNic(form.nic)) next.nic = 'Enter a valid NIC (9 digits + V, or 12 digits).';
        if (form.dateOfBirth) {
            const age = (Date.now() - new Date(`${form.dateOfBirth}T00:00:00`).getTime()) / (365.25 * 24 * 3600 * 1000);
            if (Number.isNaN(age) || age < 10 || age > 90) next.dateOfBirth = 'Please enter a valid date of birth.';
        }
        if (!isValidPhone(form.mobile)) next.mobile = 'Enter a valid mobile number, e.g. 0771234567.';
        if (form.whatsapp.trim() && !isValidPhone(form.whatsapp)) next.whatsapp = 'Enter a valid WhatsApp number, e.g. 0771234567.';
        if (!isValidEmail(form.email)) next.email = 'Please enter a valid email address.';
        if (form.city.trim().length < 2) next.city = 'Please enter your city.';
        if (!form.educationLevel) next.educationLevel = 'Please select your education level.';
        return next;
    };

    // Bring the first invalid question into view.
    const scrollToFirstError = () => {
        setTimeout(() => {
            const el = document.querySelector('[data-invalid="true"]');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
    };

    const submit = async (event) => {
        event.preventDefault();
        if (submitting) return;
        const all = findErrors();
        if (Object.keys(all).length > 0) {
            setErrors(all);
            scrollToFirstError();
            return;
        }
        setSubmitting(true);
        setServerError('');
        try {
            const res = await axios.post(`${apiBase}/sales-inquiry-form/public/${encodeURIComponent(slug)}/submit`, {
                courseToken,
                ...form,
            });
            setDoneMessage(res.data?.message || 'Thank you! We will contact you soon.');
        } catch (err) {
            const data = err?.response?.data;
            if (data?.errors) {
                setErrors(data.errors);
                scrollToFirstError();
            }
            setServerError(data?.message || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (state === 'loading') {
        return (
            <Shell>
                <Header />
                <p className="py-10 text-center text-sm text-slate-500">Loading form…</p>
            </Shell>
        );
    }

    if (state === 'invalid' || state === 'error') {
        return (
            <Shell>
                <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
                    <h1 className="text-lg font-semibold text-slate-900">
                        {state === 'invalid' ? 'This form link is not valid' : 'Unable to load the form'}
                    </h1>
                    <p className="mt-2 text-sm text-slate-500">
                        {state === 'invalid'
                            ? 'Please ask your counsellor for the correct link.'
                            : 'Please check your connection and try again.'}
                    </p>
                </div>
            </Shell>
        );
    }

    if (doneMessage) {
        return (
            <Shell>
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="h-2.5 bg-violet-600" />
                    <div className="p-6 text-center sm:p-8">
                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                            <svg className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h1 className="mt-4 text-lg font-semibold text-slate-900">Your response has been recorded</h1>
                        <p className="mt-2 text-sm text-slate-600">{doneMessage}</p>
                    </div>
                </div>
            </Shell>
        );
    }

    return (
        <Shell>
            <Header locked={lockedCourse} />

            <form onSubmit={submit} noValidate className="space-y-4">
                <Card number={1} title="Course" description="Which course would you like to know about?">
                    <Field
                        label={lockedCourse ? 'Selected course' : 'Course'}
                        required
                        wide
                        error={errors.courseToken}
                        hint={selectedCourse ? undefined : 'Search and select one course to continue.'}
                    >
                        {selectedCourse ? (
                            <div className="flex items-start justify-between gap-3 rounded-lg bg-violet-50 px-3 py-2.5">
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-violet-900">{selectedCourse.shortName ?? selectedCourse.label}</p>
                                    {selectedCourse.fullName ? (
                                        <p className="text-xs text-violet-700">{selectedCourse.fullName}</p>
                                    ) : null}
                                </div>
                                {lockedCourse ? null : (
                                    <button
                                        type="button"
                                        onClick={() => setCourseToken(null)}
                                        className="shrink-0 text-xs font-semibold text-violet-700 hover:underline"
                                    >
                                        Change
                                    </button>
                                )}
                            </div>
                        ) : (
                            <>
                                <input
                                    type="search"
                                    value={courseSearch}
                                    onChange={(e) => setCourseSearch(e.target.value)}
                                    placeholder="Search courses…"
                                    className={inputClass(false)}
                                />
                                <ul className="mt-2 max-h-64 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200">
                                    {filteredCourses.length === 0 ? (
                                        <li className="px-3 py-3 text-xs text-slate-500">No course found.</li>
                                    ) : (
                                        filteredCourses.map((course) => (
                                            <li key={course.token}>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setCourseToken(course.token);
                                                        setErrors((prev) => ({ ...prev, courseToken: undefined }));
                                                    }}
                                                    className="flex w-full items-start gap-3 px-3 py-2.5 text-left hover:bg-violet-50"
                                                >
                                                    <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 border-slate-400" />
                                                    <span className="min-w-0">
                                                        <span className="block text-sm font-medium text-slate-900">
                                                            {course.shortName ?? course.label}
                                                        </span>
                                                        {course.fullName ? (
                                                            <span className="block text-xs text-slate-500">{course.fullName}</span>
                                                        ) : null}
                                                    </span>
                                                </button>
                                            </li>
                                        ))
                                    )}
                                </ul>
                            </>
                        )}
                    </Field>
                </Card>

                {selectedCourse ? (
                    <>
                        <Card number={2} title="Personal details" description="Tell us who you are.">
                            <Field label="Full name" required wide hint="As on your NIC." error={errors.studentName}>
                                <input
                                    type="text"
                                    value={form.studentName}
                                    onChange={setField('studentName')}
                                    placeholder="e.g. Kamal Perera"
                                    autoComplete="name"
                                    maxLength={150}
                                    className={inputClass(errors.studentName)}
                                />
                            </Field>
                            <Field label="NIC number" required hint="e.g. 199012345678 or 901234567V" error={errors.nic}>
                                <input
                                    type="text"
                                    value={form.nic}
                                    onChange={setField('nic')}
                                    placeholder="NIC number"
                                    maxLength={12}
                                    className={`${inputClass(errors.nic)} uppercase`}
                                />
                            </Field>
                            <Field label="Date of birth" hint="Optional" error={errors.dateOfBirth}>
                                <input
                                    type="date"
                                    value={form.dateOfBirth}
                                    onChange={setField('dateOfBirth')}
                                    max={todayIso()}
                                    autoComplete="bday"
                                    className={inputClass(errors.dateOfBirth)}
                                />
                            </Field>
                            <Field label="Gender" wide hint="Optional" error={errors.gender}>
                                <Choices name="gender" options={GENDERS} value={form.gender} onChange={setField('gender')} />
                            </Field>
                        </Card>

                        <Card number={3} title="Contact details" description="How can our counsellor reach you?">
                            <Field label="Mobile number" required hint="We will call you on this number." error={errors.mobile}>
                                <input
                                    type="tel"
                                    inputMode="tel"
                                    value={form.mobile}
                                    onChange={setField('mobile')}
                                    placeholder="07X XXX XXXX"
                                    autoComplete="tel"
                                    maxLength={15}
                                    className={inputClass(errors.mobile)}
                                />
                            </Field>
                            <Field label="WhatsApp number" hint="Optional - only if different." error={errors.whatsapp}>
                                <input
                                    type="tel"
                                    inputMode="tel"
                                    value={form.whatsapp}
                                    onChange={setField('whatsapp')}
                                    placeholder="07X XXX XXXX"
                                    maxLength={15}
                                    className={inputClass(errors.whatsapp)}
                                />
                            </Field>
                            <Field label="Email address" required error={errors.email}>
                                <input
                                    type="email"
                                    inputMode="email"
                                    value={form.email}
                                    onChange={setField('email')}
                                    placeholder="you@example.com"
                                    autoComplete="email"
                                    maxLength={150}
                                    className={inputClass(errors.email)}
                                />
                            </Field>
                            <Field label="City" required error={errors.city}>
                                <input
                                    type="text"
                                    value={form.city}
                                    onChange={setField('city')}
                                    placeholder="Your city"
                                    autoComplete="address-level2"
                                    maxLength={100}
                                    className={inputClass(errors.city)}
                                />
                            </Field>
                        </Card>

                        <Card number={4} title="Education & other" description="A little about your background.">
                            <Field label="Highest education level" required wide error={errors.educationLevel}>
                                <Choices
                                    name="educationLevel"
                                    options={EDUCATION_LEVELS}
                                    value={form.educationLevel}
                                    onChange={setField('educationLevel')}
                                />
                            </Field>
                            <Field label="Current occupation" wide hint="Optional - e.g. student, employed">
                                <input
                                    type="text"
                                    value={form.occupation}
                                    onChange={setField('occupation')}
                                    placeholder="Your occupation"
                                    maxLength={100}
                                    className={inputClass(false)}
                                />
                            </Field>
                            <Field label="Anything you would like to ask?" wide hint="Optional">
                                <textarea
                                    value={form.message}
                                    onChange={setField('message')}
                                    placeholder="Your message"
                                    rows={3}
                                    maxLength={1000}
                                    className={`${inputClass(false)} resize-none`}
                                />
                            </Field>

                            {/* Honeypot: hidden from people, bots tend to fill it. */}
                            <input
                                type="text"
                                name="website"
                                value={form.website}
                                onChange={setField('website')}
                                tabIndex={-1}
                                autoComplete="off"
                                aria-hidden="true"
                                className="absolute -left-[9999px] h-0 w-0 opacity-0"
                            />
                        </Card>

                        {serverError ? (
                            <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{serverError}</div>
                        ) : null}

                        <div className="flex items-center justify-between gap-3 pb-4">
                            <button
                                type="submit"
                                disabled={submitting}
                                className="rounded-lg bg-violet-600 px-8 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:bg-violet-400"
                            >
                                {submitting ? 'Submitting…' : 'Submit'}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setForm(EMPTY);
                                    setErrors({});
                                    setServerError('');
                                    if (!lockedCourse) setCourseToken(null);
                                }}
                                className="text-sm font-medium text-violet-700 hover:underline"
                            >
                                Clear form
                            </button>
                        </div>
                    </>
                ) : null}
            </form>
        </Shell>
    );
}
